import { DownloadOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { JournalBulkItem, JournalBulkPreview, JournalBulkResult, JournalResponse, JournalRow } from "@wms/domain";
import { Alert, App, Button, DatePicker, Input, Modal, Select, Space, Table, Tag, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE, api, describeError, qs } from "../api/client";
import { formatDate } from "../api/format";
import { downloadHeaders } from "../api/download";
import { LoadProblem } from "../components/LoadProblem";
import { OperationTag, type OperationKind } from "../components/OperationTag";
import { PageHeader } from "../components/PageHeader";
import { RefSelect } from "../components/RefSelect";

/**
 * ТЗ 1.2 W02 «Общий журнал документов» (29.10.2026): one list over receipts, orders, counts, the documents of
 * operations C/D/E and the projected moves, defects and write-offs. The colour follows the type (UI-01), the status
 * and the next step are words (UI-03); the separate lists of each type stay where they were — this is an additional
 * entry (NAV-03). 5.2: several rows may be selected for one compatible action — the server says per document whether
 * it is allowed and why not, the page shows the count and the consequences before running, the result of every
 * document after, and retries only the failed ones.
 */
const rowKey = (row: Pick<JournalRow, "type" | "id">) => `${row.type}:${row.id}`;

export function DocumentJournalPage() {
  const { message } = App.useApp();
  const [period, setPeriod] = useState<[Dayjs | null, Dayjs | null]>([dayjs().subtract(30, "day"), dayjs()]);
  const [warehouse, setWarehouse] = useState<string | undefined>();
  const [partner, setPartner] = useState<string | undefined>();
  const [types, setTypes] = useState<string[]>([]);
  const [status, setStatus] = useState<string | undefined>();
  const [q, setQ] = useState("");
  const [product, setProduct] = useState<string | undefined>();
  const [batch, setBatch] = useState<string | undefined>();
  const [basis, setBasis] = useState("");
  const [actor, setActor] = useState("");
  const [more, setMore] = useState(false);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<React.Key[]>([]);
  const [preview, setPreview] = useState<JournalBulkPreview | null>(null);
  const [reason, setReason] = useState("");
  const [result, setResult] = useState<JournalBulkResult | null>(null);
  const params = useMemo(
    () => ({
      date_from: period[0]?.format("YYYY-MM-DD"),
      date_to: period[1]?.format("YYYY-MM-DD"),
      warehouse_id: warehouse,
      partner_id: partner,
      types: types.length ? types.join(",") : undefined,
      status,
      q: q.trim() || undefined,
      storage_object_id: product,
      batch_id: batch,
      basis: basis.trim() || undefined,
      actor: actor.trim() || undefined,
      page,
      size: 50,
    }),
    [period, warehouse, partner, types, status, q, product, batch, basis, actor, page],
  );
  const journal = useQuery({ queryKey: ["documents-journal", params], queryFn: () => api.get<JournalResponse>("/documents/journal", params) });
  const rows = journal.data?.items ?? [];
  const selectedItems: JournalBulkItem[] = rows.filter((r) => selected.includes(rowKey(r))).map((r) => ({ type: r.type, id: r.id }));

  const previewRun = useMutation({
    mutationFn: (action: string) => api.post<JournalBulkPreview>("/documents/journal/bulk/preview", { action, items: selectedItems }),
    onSuccess: (data) => {
      setReason("");
      setPreview(data);
    },
    onError: (e) => message.error(describeError(e), 6),
  });
  const execute = useMutation({
    mutationFn: (body: { action: string; items: JournalBulkItem[]; reason?: string }) => api.post<JournalBulkResult>("/documents/journal/bulk/execute", body),
    onSuccess: (data) => {
      setPreview(null);
      setResult(data);
      journal.refetch();
    },
    onError: (e) => message.error(describeError(e), 6),
  });

  if (journal.isError) return <LoadProblem error={journal.error} onRetry={() => journal.refetch()} />;
  const data = journal.data;
  const typeOptions = (data?.types ?? []).map((t) => ({ value: t.value, label: t.label }));
  const bulkActions = data?.bulk_actions ?? [];
  const exportHref = `${API_BASE}/documents/journal/export.csv${qs({ ...params, page: undefined, size: undefined })}`;

  const columns = [
    {
      title: "Номер",
      dataIndex: "number",
      render: (value: string, row: JournalRow) => <Link to={row.path} data-testid={`journal-open-${row.number}`}>{value}</Link>,
    },
    {
      title: "Тип",
      dataIndex: "type_label",
      render: (value: string, row: JournalRow) =>
        row.operation === "count" ? <Tag bordered={false}>{value}</Tag> : <OperationTag kind={row.operation as OperationKind}>{value}</OperationTag>,
    },
    { title: "Дата", dataIndex: "date", render: (value: string) => formatDate(value) },
    {
      title: "Клиент",
      dataIndex: "partner_name",
      // 3.4: the operator's own goods have no client — the row says so instead of staying blank
      render: (value: string | null, row: JournalRow) => value ?? (row.owner_kind === "operator" ? <Typography.Text type="secondary">собственный товар</Typography.Text> : ""),
    },
    { title: "Склад", dataIndex: "warehouse_code" },
    { title: "Статус", dataIndex: "status_label" },
    {
      title: "Количество",
      dataIndex: "quantity",
      align: "right" as const,
      // ANA-01: a number with its unit; several units are listed, never added up
      render: (value: string, row: JournalRow) => (row.unit === "разные" ? value : value ? `${value} ${row.unit}` : ""),
    },
    { title: "Следующий шаг", dataIndex: "next_step" },
    { title: "Автор / ответственный", dataIndex: "author", render: (value: string | null, row: JournalRow) => [value, row.responsible].filter((v, i, a) => v && a.indexOf(v) === i).join(" → ") },
  ];

  const failedItems: JournalBulkItem[] = (result?.items ?? []).filter((r) => !r.ok).map((r) => ({ type: r.type as JournalRow["type"], id: r.id }));

  return (
    <div>
      <PageHeader
        title="Журнал документов"
        subtitle="Поступления, заказы, инвентаризации, перемещения, брак и списания в одном списке: цвет — тип операции, рядом статус и следующий шаг"
        extra={
          <Button
            icon={<DownloadOutlined />}
            onClick={async () => {
              const response = await fetch(exportHref, { headers: downloadHeaders() });
              if (!response.ok) return;
              const url = URL.createObjectURL(await response.blob());
              const a = document.createElement("a");
              a.href = url;
              a.download = "documents-journal.csv";
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Выгрузить CSV
          </Button>
        }
      />
      <div className="wms-card" style={{ padding: 16 }}>
        <Space wrap style={{ marginBottom: 12 }} size={[8, 8]}>
          <DatePicker.RangePicker value={period} onChange={(v) => { setPeriod([v?.[0] ?? null, v?.[1] ?? null]); setPage(1); }} format="DD.MM.YYYY" allowClear={false} />
          <RefSelect entity="warehouses" value={warehouse} onChange={(v) => { setWarehouse(v as string | undefined); setPage(1); }} placeholder="Все доступные склады" style={{ minWidth: 200 }} />
          <RefSelect entity="partners" value={partner} onChange={(v) => { setPartner(v as string | undefined); setPage(1); }} placeholder="Клиент" style={{ minWidth: 200 }} />
          <Select mode="multiple" options={typeOptions} value={types} onChange={(v) => { setTypes(v); setPage(1); }} placeholder="Все типы" style={{ minWidth: 220 }} allowClear maxTagCount="responsive" />
          <Input.Search value={q} onChange={(e) => setQ(e.target.value)} onSearch={() => setPage(1)} placeholder="Номер, номер ERP, товар или штрихкод" style={{ width: 280 }} allowClear data-testid="journal-search" />
          <Button type="link" onClick={() => setMore((v) => !v)}>
            {more ? "Меньше фильтров" : "Ещё фильтры"}
          </Button>
        </Space>
        {more && (
          <Space wrap style={{ marginBottom: 12 }} size={[8, 8]}>
            <RefSelect entity="storage_objects" value={product} onChange={(v) => { setProduct(v as string | undefined); setPage(1); }} placeholder="Товар" style={{ minWidth: 220 }} />
            {/* 5.2 «Ещё фильтры»: the batch of the lines and the number of the basis (client request, ERP, document) */}
            <RefSelect entity="batches" value={batch} onChange={(v) => { setBatch(v as string | undefined); setPage(1); }} placeholder="Партия" style={{ minWidth: 200 }} />
            <Input value={basis} onChange={(e) => { setBasis(e.target.value); setPage(1); }} placeholder="Номер основания (заявка, ERP, документ)" style={{ width: 280 }} allowClear data-testid="journal-basis" />
            <Input value={actor} onChange={(e) => setActor(e.target.value)} placeholder="Сотрудник (логин)" style={{ width: 200 }} allowClear />
            <Input value={status ?? ""} onChange={(e) => { setStatus(e.target.value || undefined); setPage(1); }} placeholder="Код статуса (new, confirmed, shipped…)" style={{ width: 260 }} allowClear />
          </Space>
        )}
        {selected.length > 0 && (
          // 5.2: one panel for the selection — the count and only the actions the server knows how to apply
          <Space wrap style={{ marginBottom: 12 }} size={[8, 8]} data-testid="journal-bulk-panel">
            <Typography.Text strong>Выбрано: {selected.length}</Typography.Text>
            {bulkActions.map((a) => (
              <Button key={a.value} data-testid={`journal-bulk-${a.value}`} danger={a.value === "cancel"} loading={previewRun.isPending && previewRun.variables === a.value} onClick={() => previewRun.mutate(a.value)}>
                {a.label}
              </Button>
            ))}
            <Button type="link" onClick={() => setSelected([])}>Снять выделение</Button>
          </Space>
        )}
        {data?.truncated && <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Показаны самые свежие документы периода: сузьте период или тип, чтобы увидеть остальные" />}
        <Table<JournalRow>
          rowKey={rowKey}
          size="small"
          loading={journal.isFetching}
          dataSource={rows}
          columns={columns}
          rowSelection={{ selectedRowKeys: selected, onChange: setSelected, preserveSelectedRowKeys: true }}
          rowClassName={(row) => (row.operation === "count" ? "" : `wms-doc wms-doc-${row.operation}`)}
          pagination={{ current: page, pageSize: 50, total: data?.total ?? 0, onChange: setPage, showSizeChanger: false, showTotal: (t) => `документов: ${t}` }}
          scroll={{ x: true }}
          locale={{ emptyText: <Typography.Text type="secondary">За период документов нет. Измените период или фильтры.</Typography.Text> }}
        />
      </div>
      <Modal
        title={preview ? `${preview.label}: ${preview.summary.total} документов` : ""}
        open={!!preview}
        onCancel={() => setPreview(null)}
        okText={preview ? `${preview.label}: ${preview.summary.allowed}` : "Выполнить"}
        okButtonProps={{ disabled: !preview || preview.summary.allowed === 0 || (preview.summary.needs_reason && !reason.trim()), "data-testid": "journal-bulk-run" } as never}
        confirmLoading={execute.isPending}
        onOk={() =>
          preview &&
          execute.mutate({
            action: preview.action,
            items: preview.items.filter((i) => i.allowed).map((i) => ({ type: i.type as JournalRow["type"], id: i.id })),
            reason: reason.trim() || undefined,
          })
        }
        width={760}
        destroyOnHidden
      >
        {preview && (
          <>
            <Typography.Paragraph>
              Будет выполнено для <b>{preview.summary.allowed}</b> из {preview.summary.total}
              {preview.summary.by_type.length > 0 && <> ({preview.summary.by_type.map((t) => `${t.label}: ${t.count}`).join(", ")})</>}.
              {preview.summary.blocked > 0 && <> Недоступно: <b>{preview.summary.blocked}</b> — причины в таблице, эти документы пропускаются.</>}
            </Typography.Paragraph>
            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="Последствия" description={preview.summary.outcome} />
            {preview.summary.needs_reason && (
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Причина отмены — попадёт в историю каждого документа" style={{ marginBottom: 12 }} maxLength={300} data-testid="journal-bulk-reason" />
            )}
            <Table
              size="small"
              rowKey={(r) => `${r.type}:${r.id}`}
              pagination={false}
              scroll={{ y: 320 }}
              dataSource={preview.items}
              columns={[
                { title: "Номер", dataIndex: "number", render: (v: string | null) => v ?? "—" },
                { title: "Статус", dataIndex: "status_label" },
                {
                  title: "Результат",
                  render: (_: unknown, r: JournalBulkPreview["items"][number]) =>
                    r.allowed ? <Tag color="green">{r.action_label ?? "Можно"}</Tag> : <Typography.Text type="danger">{r.reason}</Typography.Text>,
                },
              ]}
            />
          </>
        )}
      </Modal>
      <Modal
        title={result ? `${result.label}: результат` : ""}
        open={!!result}
        onCancel={() => setResult(null)}
        footer={
          <Space>
            {failedItems.length > 0 && (
              <Button data-testid="journal-bulk-retry" loading={execute.isPending} onClick={() => result && execute.mutate({ action: result.action, items: failedItems, reason: reason.trim() || undefined })}>
                Повторить только неуспешные ({failedItems.length})
              </Button>
            )}
            <Button type="primary" onClick={() => { setResult(null); setSelected([]); }}>Готово</Button>
          </Space>
        }
        width={760}
        destroyOnHidden
      >
        {result && (
          <>
            <Typography.Paragraph>
              Выполнено: <b>{result.done}</b>, с ошибкой: <b>{result.failed}</b>
            </Typography.Paragraph>
            <Table
              size="small"
              rowKey={(r) => `${r.type}:${r.id}`}
              pagination={false}
              scroll={{ y: 320 }}
              dataSource={result.items}
              columns={[
                { title: "Номер", dataIndex: "number", render: (v: string | null) => v ?? "—" },
                {
                  title: "Результат",
                  render: (_: unknown, r: JournalBulkResult["items"][number]) =>
                    r.ok ? <Tag color="green">{r.status_label ?? "Выполнено"}</Tag> : <Typography.Text type="danger">{r.error}</Typography.Text>,
                },
              ]}
            />
          </>
        )}
      </Modal>
    </div>
  );
}
